import React from 'react';
import { useBookings } from '../lib/bookingsContext';
import { formatDuration, msRemaining, stateOf } from '../lib/bookings';

interface Props {
  /** Hidden on the tracker itself, where the board already says all this. */
  isTrackerActive: boolean;
  onGoToTracker: () => void;
}

/**
 * App-level alert for overdue rooms.
 *
 * The alarm rings wherever you are in the app, so it needs somewhere to send
 * you: noise with no destination just teaches people to ignore it. This names
 * the rooms and is itself the button that gets you to them.
 *
 * Consumes the booking context directly rather than taking props from App, so
 * the one-second tick re-renders this strip alone instead of the whole app.
 */
export function OverdueBanner({ isTrackerActive, onGoToTracker }: Props) {
  const { bookings, warningMinutes, nowMs } = useBookings();

  if (isTrackerActive) return null;

  const overdue = bookings.filter(s => stateOf(s, nowMs, warningMinutes) === 'expired');
  if (overdue.length === 0) return null;

  // Longest overrun first — that is the one most likely to have been forgotten.
  const sorted = [...overdue].sort((a, b) => msRemaining(a, nowMs) - msRemaining(b, nowMs));
  const shown = sorted.slice(0, 3);
  const rest = sorted.length - shown.length;

  return (
    <button type="button" className="rt-overdue-banner" onClick={onGoToTracker}>
      <span className="rt-overdue-bell">🔔</span>
      <span className="rt-overdue-text">
        <strong>
          {overdue.length} room{overdue.length === 1 ? '' : 's'} overdue
        </strong>
        <span className="rt-overdue-rooms">
          {shown.map(s => `Room ${s.roomNumber} · ${s.workingName} · ${formatDuration(msRemaining(s, nowMs))} over`).join('   ')}
          {rest > 0 && `   +${rest} more`}
        </span>
      </span>
      <span className="rt-overdue-go">Go to Room tracker →</span>
    </button>
  );
}
